import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { History } from 'history';
import { Row, Col, Spin, message as Message, Modal } from 'antd';
import { FaRegClock, FaGift } from 'react-icons/fa';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { getMealState } from '../../../store/meals/selector';
import { getAppGlobalState } from '../../../store/app-globals/selectors';
import { getMyMeals, deleteMeal } from '../../../store/meals/actions';
import { Meal } from '../../../store/meals/types';

import { PageHeader } from '../../layout/PageHeader';
import { ActionButtons } from './ActionButtons';
import { EmptyContainer } from '../../layout/EmptyContainer';
import { GlobalModalClose } from '../../layout/GlobalModal';
import { GlobalModalProps } from '../../orders/PlaceOrder';
import { getScheduleTime } from '../../dashboard/components/MealCarousel';

import Untensil from '../../../assets/images/png/utensil.png';
import Table from '../../../assets/images/png/restaurant-table.png';
import EmptyMealIcon from '../../../assets/images/png/fork.png';
import { getUserState } from '../../../store/user/selector';

export interface MealParams {
  meal?: Meal;
  onPressDelete?: (e?: React.MouseEvent) => void;
  handleFieldChange?: (e: React.FormEvent<HTMLInputElement>) => void;
}

const meal: Meal = {
  id: '',
  title: '',
  price: '',
  portions: 0,
  consumption: '',
  cook: '',
  visible: false,
  schedule: [],
  timeadded: {},
  category: [],
  restrictions: [],
};

export const MealContext = React.createContext<MealParams>({ meal });

export const TimePortionsPrice: React.FC = () => {
  const { t } = useTranslation();
  const { meal } = React.useContext(MealContext);

  const isStable = meal?.stableoffer?.isChecked;
  const getFirstSchedule = _.head(meal?.schedule);
  const getFirstScheduleTime = _.head(getFirstSchedule?.time);
  const time = getScheduleTime(getFirstScheduleTime);
  return (
    <Col span={24} className="extra-details">
      <div>
        <div className="center-align">
          <FaRegClock color="#A8A8A8" />
          <span>{isStable ? t('CONSTANT_OFFER') : time}</span>
        </div>
        <div className="center-align">
          <FaGift color="#A8A8A8" />
          <span>{`${meal?.portions} ${t('Quantity')} ${t('Available')}`}</span>
        </div>
      </div>
      <div className="price">{`Kč ${Number(meal?.price).toFixed(2)}`}</div>
    </Col>
  );
};

export const Consumption: React.FC = () => {
  const { meal } = React.useContext(MealContext);
  const { t } = useTranslation();
  const BOTH = meal?.consumption === 'Both';
  const EAT_WITH = BOTH || meal?.consumption === 'Eat with';
  const TAKE_AWAY = BOTH || meal?.consumption === 'Take away';

  return (
    <Col span={24} className="extra-details">
      {EAT_WITH && (
        <div className="center-align">
          <img src={Table} alt="untesil"></img>
          <span>{`${t('Eat with')} max ${meal?.maxtime} mins`}</span>
        </div>
      )}
      {TAKE_AWAY && (
        <div className="center-align">
          <img src={Untensil} alt="untesil"></img>
          <span>{t('Take away')}</span>
        </div>
      )}
    </Col>
  );
};

export const MyMealDetails: React.FC = () => {
  const { isLoading, hasError } = useSelector(getAppGlobalState);
  const { user } = useSelector(getUserState);
  // const { meals: offers } = useSelector(getAdminState);
  const { meals } = useSelector(getMealState);
  const { id } = useParams();
  const dispatch = useDispatch();
  const history: History = useHistory();
  const meal = meals.find(i => i.id === id);
  const { t } = useTranslation();
  // console.log(user);
  const validateMeal = useRef(_.isEmpty(meals));
  const isDeletingMeal = useRef(false);
  let reason = '';
  const handleFieldChange = (e: React.FormEvent<HTMLInputElement>) => {
    reason = e.currentTarget.value;
  };
  const onPressDelete = () => {
    dispatch(
      deleteMeal({
        mealid: meal?.id,
        reason,
        cookid: meal?.cook,
        mealname: meal?.title,
      })
    );
    isDeletingMeal.current = true;
  };
  const deletingMeal = () => {
    if (isDeletingMeal.current && !isLoading && !hasError) {
      GlobalModalClose();
      const deletedPrompt = Modal.success({
        ...GlobalModalProps,
        className: 'modal-deleted-prompt',
        style: { top: 5, textAlign: 'center' },
        content: (
          <div className="modal-deleted-prompt-content">
            The offer has been deleted
          </div>
        ),
      });
      if (user.accountType === 'admin') {
        history.push('/manageuser');
      }
      history.goBack();
      setTimeout(() => {
        deletedPrompt.destroy();
      }, 3000);
    }
    if (isDeletingMeal.current && !isLoading && hasError) {
      Message.error('Somwething went wrong');
    }
  };
  useEffect(() => {
    if (_.isEmpty(meals)) {
      dispatch(getMyMeals());
    }
    validateMeal.current = _.isEmpty(meals);
  }, [validateMeal, meals, isLoading, dispatch]);
  useEffect(deletingMeal, [isLoading]);
  return (
    <MealContext.Provider value={{ meal, handleFieldChange, onPressDelete }}>
      <div className="my-meals-container">
        <PageHeader headerTitle="My Offer">
          <ActionButtons />
        </PageHeader>
        <Row justify="center" className="my-meals-inner-container">
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={24}
            // xl={10}
            className="my-meals-wrapper"
          >
            {validateMeal.current ? (
              <Row justify="center" className="spinner-container">
                <Spin />
              </Row>
            ) : (
              <React.Fragment>
                {meal ? (
                  <Col span={22} className="meal-details-wrapper">
                    <Col
                      span={24}
                      className="meal-image"
                      style={{
                        backgroundImage: `url(${meal?.image})`,
                        backgroundPosition: 'center',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                      }}
                    ></Col>
                    <TimePortionsPrice />
                    <Col span={24} className="meal-main-description">
                      <div className="title">{meal?.title}</div>
                      <div className="description">{meal?.description}</div>
                    </Col>
                    <Consumption />
                  </Col>
                ) : (
                  <EmptyContainer
                    title={t('MEAL_NOT_EXISTS')}
                    icon={EmptyMealIcon}
                  />
                )}
              </React.Fragment>
            )}
          </Col>
        </Row>
      </div>
    </MealContext.Provider>
  );
};
